import { tt } from '@agos/shared'
import { AlertCircle } from 'lucide-react'
import { Link } from 'wouter'

/**
 * The red count of orders waiting to be accepted on *Rozvoz - nástenka* - FRONTEND.md §9.674, owner O58.
 *
 * **The owner**: *"keď príde nová obj dať tam niečo výkričník číslo koľko je neprijatých obj ? Červeným aby to
 * upozornilo"*. Two forms of one number: on the sidebar entry, and in the header for whenever the sidebar is not
 * on screen. Both take the text `badgeText` already wrote, so nought draws nothing and above 99 reads `99+` -
 * the bell's rule, and the bell's red.
 *
 * **Here rather than inline in `AppLayout`** so a test can draw them without mounting the shell, whose module
 * registry imports every manager module.
 */

/**
 * The count on a sidebar entry. Nothing at all for an empty text - a red nought would never go away.
 *
 * `data-sidebar-count` rather than `data-nav-badge`, which the customer app's bottom bar already carries: one
 * marker naming two things in two apps is how a spec ends up asserting about the wrong one.
 */
export function NavCountBadge({ text, path }: { text: string; path: string }) {
  if (text === '') return null
  return (
    <span
      data-sidebar-count={path}
      title={tt('Neprijaté objednávky')}
      className="ml-auto shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold tabular-nums flex items-center justify-center"
    >
      {text}
    </span>
  )
}

/**
 * The same count in the header, as a link to the board.
 *
 * **The exclamation mark is the owner's *výkričník*.** On the sidebar the entry's own label says what the
 * number counts; up here it stands beside the *Rozvoz* / *Nástenka* links with no label of its own, so the mark
 * is what says *something is waiting* and the accessible name says what.
 *
 * `onClick` is the layout's `askFirst`, so following it asks before it takes a screen's unsaved work, like every
 * other navigating control in the shell (§9.631).
 *
 * **Compact, because the phone header has little room left.** By its classes the right-hand group is about
 * 256 px of the 358 a 390 px phone leaves inside `px-4` - two 32 px surface links, the 32 px archive shortcut,
 * the 36 px bell, the 36 px theme toggle and a 56 px language control, with their gaps. This pill is about 35 px
 * for one digit, so the row still fits with a two-digit count. An estimate from the classes, not a measurement in
 * a browser.
 */
export function UnacceptedOrdersLink({
  text,
  href,
  onClick,
}: {
  text: string
  href: string
  onClick: (event: { preventDefault: () => void }) => void
}) {
  if (text === '') return null
  const label = `${tt('Neprijaté objednávky')}: ${text}`
  return (
    <Link
      href={href}
      data-unaccepted-link
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex items-center gap-0.5 h-8 min-w-8 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-black tabular-nums shadow-sm"
    >
      <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      {text}
    </Link>
  )
}
